import React, { useState, useRef, useCallback } from 'react';
import { Upload, Camera, X, Check, Loader2, Image as ImageIcon, Plus, Trash2 } from 'lucide-react';
import { Button } from '../ui';
import { morningPagesService } from '../../services';
import './PhotoUpload.css';

interface SelectedFile {
  file: File;
  preview: string;
  id: string;
}

interface UploadStatus {
  id: string;
  progress: number;
  status: 'pending' | 'uploading' | 'completed' | 'error';
  error?: string;
  result?: { photoUrl: string; pageId: string };
}

interface PhotoUploadProps {
  onUploadComplete?: (photoUrl: string, pageId: string) => void;
  onAllUploadsComplete?: (results: Array<{ photoUrl: string; pageId: string }>) => void;
  onCancel?: () => void;
  date?: string;
}

export const PhotoUpload: React.FC<PhotoUploadProps> = ({
  onUploadComplete,
  onAllUploadsComplete,
  onCancel,
  date,
}) => {
  const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([]);
  const [uploadStatuses, setUploadStatuses] = useState<Map<string, UploadStatus>>(new Map());
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const generateId = () => Math.random().toString(36).substring(2, 9);

  const handleFileSelect = useCallback(async (files: FileList | File[]) => {
    setError(null);
    const fileArray = Array.from(files);
    const newFiles: SelectedFile[] = [];

    for (const file of fileArray) {
      // Validate file
      const validation = morningPagesService.validateFile(file);
      if (!validation.valid) {
        setError(validation.error || 'Invalid file');
        continue;
      }

      // Compress image if needed
      try {
        const compressedFile = await morningPagesService.compressImage(file);
        const id = generateId();

        // Create preview
        const preview = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target?.result as string);
          reader.readAsDataURL(compressedFile);
        });

        newFiles.push({ file: compressedFile, preview, id });
      } catch (err) {
        setError('Failed to process one or more images');
      }
    }

    if (newFiles.length > 0) {
      setSelectedFiles((prev) => [...prev, ...newFiles]);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        handleFileSelect(files);
      }
    },
    [handleFileSelect]
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        handleFileSelect(files);
      }
      // Reset input so same files can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [handleFileSelect]
  );

  const handleRemoveFile = (id: string) => {
    setSelectedFiles((prev) => prev.filter((f) => f.id !== id));
    setUploadStatuses((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    setIsUploading(true);
    setError(null);

    // Initialize all statuses
    const initialStatuses = new Map<string, UploadStatus>();
    selectedFiles.forEach((sf) => {
      initialStatuses.set(sf.id, { id: sf.id, progress: 0, status: 'pending' });
    });
    setUploadStatuses(initialStatuses);

    const results: Array<{ photoUrl: string; pageId: string }> = [];

    // Upload files sequentially to avoid overwhelming the server
    for (const selectedFile of selectedFiles) {
      // Update status to uploading
      setUploadStatuses((prev) => {
        const next = new Map(prev);
        next.set(selectedFile.id, { ...next.get(selectedFile.id)!, status: 'uploading', progress: 0 });
        return next;
      });

      try {
        // Simulate progress for better UX
        const progressInterval = setInterval(() => {
          setUploadStatuses((prev) => {
            const next = new Map(prev);
            const current = next.get(selectedFile.id);
            if (current && current.progress < 90) {
              next.set(selectedFile.id, { ...current, progress: current.progress + 10 });
            }
            return next;
          });
        }, 200);

        const result = await morningPagesService.uploadMorningPage({
          photo: selectedFile.file,
          date,
        });

        clearInterval(progressInterval);

        // Update status to completed
        setUploadStatuses((prev) => {
          const next = new Map(prev);
          next.set(selectedFile.id, {
            id: selectedFile.id,
            progress: 100,
            status: 'completed',
            result: { photoUrl: result.photoUrl, pageId: result.id },
          });
          return next;
        });

        results.push({ photoUrl: result.photoUrl, pageId: result.id });
        onUploadComplete?.(result.photoUrl, result.id);
      } catch (err) {
        // Update status to error
        setUploadStatuses((prev) => {
          const next = new Map(prev);
          next.set(selectedFile.id, {
            id: selectedFile.id,
            progress: 0,
            status: 'error',
            error: err instanceof Error ? err.message : 'Upload failed',
          });
          return next;
        });
      }
    }

    setIsUploading(false);

    // Call the all uploads complete callback if provided
    if (results.length > 0) {
      // Small delay to show completion state
      setTimeout(() => {
        onAllUploadsComplete?.(results);
      }, 500);
    }
  };

  const handleClear = () => {
    setSelectedFiles([]);
    setUploadStatuses(new Map());
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const completedCount = Array.from(uploadStatuses.values()).filter((s) => s.status === 'completed').length;
  const errorCount = Array.from(uploadStatuses.values()).filter((s) => s.status === 'error').length;
  const allCompleted = completedCount === selectedFiles.length && selectedFiles.length > 0;

  return (
    <div className="photo-upload">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
        onChange={handleInputChange}
        className="photo-upload__input"
        aria-label="Select photos"
        multiple
      />

      {selectedFiles.length === 0 ? (
        <div
          className={`photo-upload__dropzone ${isDragging ? 'photo-upload__dropzone--dragging' : ''}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
        >
          <div className="photo-upload__dropzone-content">
            <div className="photo-upload__icon-wrapper">
              <ImageIcon size={48} />
            </div>
            <p className="photo-upload__title">
              Upload your morning pages
            </p>
            <p className="photo-upload__subtitle">
              Drag and drop or click to select multiple photos
            </p>
            <div className="photo-upload__buttons">
              <Button
                variant="primary"
                size="md"
                onClick={(e) => {
                  e.stopPropagation();
                  fileInputRef.current?.click();
                }}
              >
                <Upload size={18} />
                Choose Files
              </Button>
              <Button
                variant="secondary"
                size="md"
                onClick={(e) => {
                  e.stopPropagation();
                  // Camera capture would work on mobile
                  fileInputRef.current?.click();
                }}
              >
                <Camera size={18} />
                Take Photos
              </Button>
            </div>
            <p className="photo-upload__hint">
              Supports JPEG, PNG, WebP (max 20MB each)
            </p>
          </div>
        </div>
      ) : (
        <div className="photo-upload__preview-container">
          {/* File count header */}
          <div className="photo-upload__header">
            <span className="photo-upload__count">
              {selectedFiles.length} page{selectedFiles.length !== 1 ? 's' : ''} selected
              {isUploading && ` • ${completedCount}/${selectedFiles.length} uploaded`}
              {!isUploading && errorCount > 0 && ` • ${errorCount} failed`}
            </span>
            {!isUploading && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
              >
                <Plus size={16} />
                Add More
              </Button>
            )}
          </div>

          {/* Thumbnails grid */}
          <div className="photo-upload__thumbnails">
            {selectedFiles.map((sf) => {
              const status = uploadStatuses.get(sf.id);
              return (
                <div
                  key={sf.id}
                  className={`photo-upload__thumbnail ${status?.status === 'completed' ? 'photo-upload__thumbnail--completed' : ''} ${status?.status === 'error' ? 'photo-upload__thumbnail--error' : ''}`}
                >
                  <img
                    src={sf.preview}
                    alt="Morning page preview"
                    className="photo-upload__thumbnail-image"
                  />

                  {/* Upload overlay */}
                  {status?.status === 'uploading' && (
                    <div className="photo-upload__thumbnail-overlay">
                      <Loader2 size={24} className="photo-upload__spinner" />
                      <span className="photo-upload__thumbnail-progress">{status.progress}%</span>
                    </div>
                  )}

                  {/* Completed overlay */}
                  {status?.status === 'completed' && (
                    <div className="photo-upload__thumbnail-overlay photo-upload__thumbnail-overlay--success">
                      <Check size={24} />
                    </div>
                  )}

                  {/* Error overlay */}
                  {status?.status === 'error' && (
                    <div className="photo-upload__thumbnail-overlay photo-upload__thumbnail-overlay--error">
                      <X size={24} />
                    </div>
                  )}

                  {/* Remove button (only when not uploading) */}
                  {!isUploading && status?.status !== 'completed' && (
                    <button
                      className="photo-upload__thumbnail-remove"
                      onClick={() => handleRemoveFile(sf.id)}
                      aria-label="Remove photo"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {error && (
            <div className="photo-upload__error">
              <span>{error}</span>
              <button onClick={() => setError(null)} aria-label="Dismiss error">
                <X size={16} />
              </button>
            </div>
          )}

          <div className="photo-upload__actions">
            <Button
              variant="secondary"
              size="md"
              onClick={handleClear}
              disabled={isUploading}
            >
              <X size={18} />
              Clear All
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={handleUpload}
              disabled={isUploading || allCompleted}
              loading={isUploading}
            >
              <Check size={18} />
              {isUploading
                ? `Uploading ${completedCount + 1}/${selectedFiles.length}...`
                : allCompleted
                  ? 'All Uploaded!'
                  : `Upload ${selectedFiles.length} Page${selectedFiles.length !== 1 ? 's' : ''}`}
            </Button>
          </div>
        </div>
      )}

      {onCancel && !isUploading && (
        <button className="photo-upload__cancel" onClick={onCancel}>
          Cancel
        </button>
      )}
    </div>
  );
};

export default PhotoUpload;
